/**
 * RicercaFuorimenuView view component.
 * @module components/View/RicercaFuorimenuView
 */

import { getUser, getVocabulary, searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import jwtDecode from 'jwt-decode';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  Form,
  Icon,
  Input,
  Pagination,
  Loader,
  Dimmer,
} from 'semantic-ui-react';
import { getExportedMenus } from '../../actions';
import TitleStandard from '../Commons/TitleStandard';
import CardSearch from './Commons/CardSearch';
import TextBlocks from './Commons/RichTextBlocks/TextBlocks';
import useSearchFilterState from '../../hooks/useSearchFilterState';
import { hasValue, seededOrder, tokenMatch } from '../../helpers/SearchHelper';

const MAX_ITEMS = 6;

const VOCAB_CONSEGNA = 'baccanale.policy.vocabulary.tipologia_consegna';
const VOCAB_MENU_UNICO = 'baccanale.policy.vocabulary.tipologia_menu_unico';

// Historical indices, with no 0 or 2. Kept as they are to avoid CSS churn.
const SECTION_MENU_UNICO = 1;
const SECTION_CONSEGNA = 3;

const PARAM_MAP = {
  menu_unico: { key: 'menu_unico', kind: 'list' },
  consegna: { key: 'consegna', kind: 'list' },
  nome: { key: 'nome', kind: 'text' },
};

const ACCORDION_FOR_FILTER = {
  menu_unico: SECTION_MENU_UNICO,
  consegna: SECTION_CONSEGNA,
};

// This view lists the "fuori menu" items, i.e. the ones that have a
// tipologia_menu_unico. Applied to every combination of filters, not just some.
const isFuoriMenu = (menu) => menu?.tipologia_menu_unico?.length > 0;

const PREDICATES = {
  menu_unico: (menu, values) => tokenMatch(menu?.tipologia_menu_unico, values),
  consegna: (menu, values) => tokenMatch(menu?.tipologia_consegna, values),
  nome: (menu, text) =>
    (menu?.ristorante?.title || '').toLowerCase().includes(text.toLowerCase()),
};

export const applyFilters = (items, filter) =>
  (items || []).filter(
    (menu) =>
      isFuoriMenu(menu) &&
      Object.entries(filter).every(
        ([key, value]) =>
          !hasValue(value) || !PREDICATES[key] || PREDICATES[key](menu, value),
      ),
  );

const getColor = (type) => {
  switch (type) {
    case 'ristoranti-e-osterie':
      return 'yellow';
    case 'caffetterie-gastronomie':
      return 'green';
    case 'agriturismi':
      return 'red';
    default:
      return undefined;
  }
};

/**
 * RicercaFuorimenuView view component class.
 * @function RicercaFuorimenuView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const RicercaFuorimenuView = ({ content }) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const vocabularies = useSelector((state) => state?.vocabularies);
  const requestMenus = useSelector(
    (state) => state?.search?.subrequests?.menus,
  );
  const token = useSelector((state) => state?.userSession?.token);
  const users = useSelector((state) => state?.users);
  const menus = requestMenus?.items;

  const userId = token ? jwtDecode(token).sub : null;

  const {
    filter,
    page,
    seed,
    activeIndex,
    setValue,
    setText,
    setPage,
    toggleAccordion,
    isChecked,
    getText,
  } = useSearchFilterState({
    storageKey: 'ricerca_fuorimenu',
    paramMap: PARAM_MAP,
    accordionForFilter: ACCORDION_FOR_FILTER,
  });

  useEffect(() => {
    if (!users?.get?.loading && userId) {
      dispatch(getUser(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: VOCAB_CONSEGNA }));
    dispatch(getVocabulary({ vocabNameOrURL: VOCAB_MENU_UNICO }));
    dispatch(
      searchContent(
        flattenToAppURL(content['id']),
        {
          portal_type: ['Menu'],
          review_state: 'published',
          fullobjects: 1,
          b_size: 10000,
        },
        'menus',
      ),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, content['@id']]);

  // Order once, on the whole set, before filtering: that way the filtered list is
  // always a slice of one stable permutation, so the same seed and filters bring
  // back the same page of cards.
  const ordered = useMemo(() => seededOrder(menus, seed), [menus, seed]);
  const results = useMemo(() => applyFilters(ordered, filter), [
    ordered,
    filter,
  ]);

  const totalPages = Math.max(1, Math.ceil(results.length / MAX_ITEMS));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paged = useMemo(
    () => results.slice((safePage - 1) * MAX_ITEMS, safePage * MAX_ITEMS),
    [results, safePage],
  );

  const downloadFile = () => {
    dispatch(getExportedMenus(pathname));
  };

  return (
    <>
      <TitleStandard title={content.title} />

      <div className="menu-view ui container ricerca-menu">
        <div
          className={`printPDF ${
            !users?.user?.roles?.includes('Manager') ? 'hidden' : ''
          }`}
        >
          <button className="printButton" onClick={downloadFile}>
            Export RTF
          </button>
        </div>
        <div className="mt-2">
          <TextBlocks content={content} />
        </div>
        <div className="flex mt-5 mb-5 flex-wrap wrapper">
          <div style={{ flex: 1 }}>
            <Accordion fluid styled>
              <Accordion.Title
                active={activeIndex.includes(SECTION_MENU_UNICO)}
                index={SECTION_MENU_UNICO}
                onClick={(e, { index }) => toggleAccordion(index)}
                className="mt-05"
              >
                <Icon name="dropdown" />
                COSA VUOI GUSTARE?
              </Accordion.Title>
              <Accordion.Content
                active={activeIndex.includes(SECTION_MENU_UNICO)}
              >
                <Form>
                  <Form.Group grouped>
                    {vocabularies?.[VOCAB_MENU_UNICO]?.items?.map(
                      (i, index) => (
                        <Form.Checkbox
                          key={index}
                          label={i.label}
                          name={VOCAB_MENU_UNICO}
                          value={i.value}
                          checked={isChecked('menu_unico', i.value)}
                          onChange={(e, { checked }) => {
                            setValue('menu_unico', i.value, checked);
                          }}
                        />
                      ),
                    )}
                  </Form.Group>
                </Form>
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex.includes(SECTION_CONSEGNA)}
                index={SECTION_CONSEGNA}
                onClick={(e, { index }) => toggleAccordion(index)}
                className="mt-05"
              >
                <Icon name="dropdown" />
                CONSEGNA/ASPORTO
              </Accordion.Title>
              <Accordion.Content
                active={activeIndex.includes(SECTION_CONSEGNA)}
              >
                <Form>
                  <Form.Group grouped>
                    {vocabularies?.[VOCAB_CONSEGNA]?.items?.map((i, index) => (
                      <Form.Checkbox
                        key={index}
                        label={i.label}
                        name={VOCAB_CONSEGNA}
                        value={i.value}
                        checked={isChecked('consegna', i.value)}
                        onChange={(e, { checked }) => {
                          setValue('consegna', i.value, checked);
                        }}
                      />
                    ))}
                  </Form.Group>
                </Form>
              </Accordion.Content>
            </Accordion>
            <div className="mt-2">
              Oppure cerca per nome...
              <Input
                icon="search"
                placeholder="Nome del ristorante"
                className="w-100 mt-05"
                value={getText('nome')}
                onChange={(e, { value }) => {
                  setText('nome', value);
                }}
              />
            </div>
          </div>
          {requestMenus && (
            <div className="flex justify-between flex-wrap cards">
              <Dimmer active={requestMenus?.loading && !menus?.length} inverted>
                <Loader
                  active={requestMenus?.loading}
                  size={'medium'}
                  inline="centered"
                >
                  Caricamento ...
                </Loader>
              </Dimmer>

              {paged.map((m, index) => (
                <CardSearch
                  key={index}
                  anno={m.anno}
                  titolo={m.title}
                  titolo2={m.ristorante?.title}
                  linkTo={m['@id']}
                  badgeColor={getColor(
                    m.ristorante?.tipologia_ristorante?.token,
                  )}
                />
              ))}

              {totalPages > 1 && (
                <Pagination
                  activePage={safePage}
                  siblingRange={0}
                  firstItem={null}
                  lastItem={null}
                  prevItem={undefined}
                  nextItem={undefined}
                  ellipsisItem="-"
                  totalPages={totalPages}
                  onPageChange={(e, data) => {
                    window.scrollTo(0, 400);
                    setPage(data.activePage);
                  }}
                  className="pagination w-100 flex justify-center"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RicercaFuorimenuView;
